import {computed, defineComponent, watch} from 'vue';
import { InvoiceDetails } from "@/app/checkout/components/invoice-details";
import { escape } from "html-escaper";
import { useUserStore } from "@/app/shared/state/UserDataModule";
import {
    klValidationFields,
    ValidationFieldsType
} from '@/app/shared/components/kl-form-fields/kl-field-validation-util';
import * as yup from 'yup';
import {createAddressSchema} from '@/app/shared/components/kl-address-form/kl-address-form';
import {isEqual} from 'lodash-es';
import {Mri} from '@/app/mri/shared/services/services';

export const createInvoiceDetailsSchema= () => {
    const sepaValidationRegex = /^[a-zA-Z0-9\/\-?:().,'+\s]+$/i;
    return yup.object({
        vatNumberRequired: yup.boolean().notRequired(),
        vatNumber: yup
            .string().notRequired()
            .when('vatNumberRequired', {
                is: true,
                then: schema => schema.required().max(20).kl_vatNumberValid()
            })
            .label('BTW-nummer'),
        emailAddress: yup.string().required().email().label('Facturatie e-mailadres'),
        ownReference: yup.string().notRequired().max(25).matches(sepaValidationRegex, { excludeEmptyString: true }).label('Eigen referentie'),
        useAlternativeEmail: yup.boolean().notRequired(),
        address: createAddressSchema(),
    });
}

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlCheckoutInvoiceDetailsAthumi',
    emits: ['update:modelValue'],
    props: {
        modelValue: {
            type: Object as () => InvoiceDetails,
            required: true,
        },
        formPath: { type: String, required: true },
    },
    setup(props,  {emit}) {

        const user = useUserStore().user;
        const isCitizen = computed((): boolean => !!user.isCitizen);

        const schema = createInvoiceDetailsSchema();
        const fields: ValidationFieldsType<typeof schema> = klValidationFields<typeof schema>(schema, props.formPath);

        const vatNumberRequired= fields.vatNumberRequired.getValue<boolean>();
        const vatNumber= fields.vatNumber.getValue<string>();
        const emailAddress= fields.emailAddress.getValue<string>();
        const ownReference= fields.ownReference.getValue<string>();
        const useAlternativeEmail= fields.useAlternativeEmail.getValue<boolean>();
        const address= fields.address.getValue<Mri.IAddress>();

        const formResult = computed((): InvoiceDetails => ({
                ...props.modelValue,
                vatNumber: vatNumber.value,
                vatNumberRequired: vatNumberRequired.value,
                emailAddress: emailAddress.value,
                ownReference: ownReference.value,
                useAlternativeEmail: useAlternativeEmail.value,
                address: address.value,
        }));

        watch(
            formResult,
            (newValue: InvoiceDetails, oldValue: InvoiceDetails) => {
                if (!isEqual(newValue, oldValue)){
                    emit('update:modelValue', newValue)
                }
            },
            { immediate: false, deep: true });

        const _vatNumberRequired = computed((): boolean => !user.organisationKboNumber && !isCitizen.value);
        watch(_vatNumberRequired, (newValue: boolean) => { vatNumberRequired.value = newValue; }, { immediate: true });


        const emailAddressAnnotation = computed(() => {
            return isCitizen.value
                ? ''
                : 'De facturen worden in eerste instantie via het Peppol-netwerk verzonden. Als dat niet lukt, ontvangt u de factuur per e-mail.';
        });

        const ownReferenceAnnotation = computed(() => {
            return isCitizen.value
                ? ''
                : 'Optioneel kan u hier een intern bestelbon- of PO-nummer meegeven.';
        });

        const showOwnReference = computed(() => {
            return !isCitizen.value && !props.modelValue.isTopupPrepaid;
        });

        const title = computed(() => {
            if (isCitizen.value) {
                return `${escape(user.firstName)} ${escape(user.lastName)}`;
            } else {
                const ovoOrKboOrOrganisationCode = user.organisationOvoCode ? `${escape(user.organisationOvoCode)}` : user.organisationKboNumber ? `${escape(user.organisationKboNumber)}` : `${escape(user.organisationCode)}`;
                return `${escape(user.organisation)} - ${ovoOrKboOrOrganisationCode}`;
            }
        });

        const subtitle = computed((): string => {
            if (isCitizen.value) {
                return null;
            } else if (!!user.organisationVatNumber) {
                return `BTW ${escape(user.organisationVatNumber)}`;
            }
            return null;
        });

        return {
            vatNumberRequired,
            vatNumber,
            emailAddress,
            useAlternativeEmail,
            ownReference,
            address,

            title,
            subtitle,
            isCitizen,
            emailAddressAnnotation,
            showOwnReference,
            ownReferenceAnnotation,

            fields,
        }
    }
});
